import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Navigate, useSearchParams } from "react-router-dom";
import { UserPlus, Users } from "lucide-react";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import AddUserForm from "../components/AddUserForm";
import ManageUserTable from "../components/ManageUserTable";
import { useCan } from "../hooks/useCan";

const UserManagementPage = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  // Permission checks
  const { can: canViewUsers } = useCan("user", "view");
  const { can: canCreateUser } = useCan("user", "add_edit");

  // Initialize tab from URL parameter, default to 'add' (Add User)
  const tabParam = searchParams.get("tab");
  const initialTab = tabParam === "manage" ? 1 : 0;
  const [userManagementTab, setUserManagementTab] = useState(initialTab); // 0 = Add User, 1 = Manage User

  // Update tab when URL parameter changes
  useEffect(() => {
    const tabParam = searchParams.get("tab");
    if (tabParam === "manage") {
      setUserManagementTab(1);
    } else if (tabParam === "add" || !tabParam) {
      setUserManagementTab(0);
    }
  }, [searchParams]);

  // Check permissions - chief-admin always has access, others need permission
  if (user?.role !== "chief-admin" && !canViewUsers) {
    return <Navigate to="/projects" replace />;
  }

  const handleUserCreated = () => {
    // Switch to Manage User tab after successful creation
    setUserManagementTab(1);
  };

  return (
    <div className=" pb-6">
      {/* Page Header */}
      <div className="flex justify-between items-start pt-4 relative">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">
            User Management
          </h1>
          <p className="text-text-secondary mt-1">Create and manage users</p>
        </div>
      </div>

      {/* User Management Tabs */}
      <div className="card relative z-0">
        <Tabs
          value={userManagementTab === 0 ? "add" : "manage"}
          onValueChange={(value) => {
            const newValue = value === "manage" ? 1 : 0;
            setUserManagementTab(newValue);
            setSearchParams({ tab: value });
          }}
          className="w-full"
        >
          <TabsList className="grid w-full grid-cols-2 border-b border-border-primary">
            {canCreateUser && (
              <TabsTrigger value="add" className="flex items-center space-x-2">
                <UserPlus className="w-4 h-4" />
                <span>Add User</span>
              </TabsTrigger>
            )}
            {canViewUsers && (
              <TabsTrigger
                value="manage"
                className="flex items-center space-x-2"
              >
                <Users className="w-4 h-4" />
                <span>Manage User</span>
              </TabsTrigger>
            )}
          </TabsList>

          {canCreateUser && (
            <TabsContent value="add" className="p-6">
              <AddUserForm onSuccess={handleUserCreated} />
            </TabsContent>
          )}
          {canViewUsers && (
            <TabsContent value="manage" className="p-6">
              <ManageUserTable />
            </TabsContent>
          )}
        </Tabs>
      </div>
    </div>
  );
};

export default UserManagementPage;
